// src/components/Multiplayer/CustomRoomModal.jsx
// Custom Room entry: choose "Create" or "Join", then fill the form.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useAuth } from '../../contexts/AuthContext';
import './CustomRoomModal.css';
import currencyService from '../../services/CurrencyService';
import { useWallet } from '../../contexts/WalletContext';

// Map a currency code to the wallet balance key (non-USD/INR/LRD locals live in `LOCAL`).
const getBalanceKey = (currency) => {
  const c = (currency || 'USD').toUpperCase();
  if (c === 'USD') return 'USD';
  if (c === 'INR') return 'INR';
  if (c === 'LRD') return 'LRD';
  return 'LOCAL';
};

const AMOUNTS = [2, 3, 4, 5, 6, 7, 8, 9, 10];
// Every game room (free play, cash, custom) is played by 2, 4 or 6 players.
const PLAYER_COUNTS = [2, 4, 6];
const POINTS = [101, 201, 301];
// Points Rummy: USD per point, 2 or 6 players, one hand, max 80 points (nothing charged up front)
const RATE_AMOUNTS = [0.05, 0.10, 0.20, 0.25, 0.50, 1.00];
const RATE_PLAYER_COUNTS = PLAYER_COUNTS;
const RATE_MAX_POINTS = 80;

const CustomRoomModal = ({ onClose }) => {
  const navigate = useNavigate();
  const { isConnected, emitWithPromise } = useWebSocket();
  const { currentUser } = useAuth();
  const { walletBalances, walletData } = useWallet();

  const [mode, setMode] = useState(null);
  const [amount, setAmount] = useState(null);
  const [players, setPlayers] = useState(null);
  const [points, setPoints] = useState(null);
  const [gameMode, setGameMode] = useState('points');   // 'points' = Pool Rummy, 'rate' = Points Rummy
  const isRate = gameMode === 'rate';
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [joinCurrency, setJoinCurrency] = useState('USD');

  // Determine the user's local currency from their country (same logic as the Header).
  const getLocalCurrency = () => {
    const userCurrency = String(currentUser?.currencyCode || currentUser?.currency || '').toUpperCase();
    if (userCurrency && userCurrency !== 'USD' && userCurrency !== 'LOCAL') return userCurrency;
    const countryCode = String(walletData?.countryCode || currentUser?.country || '').toUpperCase();
    if (countryCode === 'BJ') return 'CFA';
    if (countryCode === 'LR' || countryCode === 'LBR') return 'LRD';
    if (countryCode === 'GM') return 'GMD';
    if (countryCode === 'IN') return 'INR';
    return 'USD';
  };

  const localCurrency = getLocalCurrency();
  const availableCurrencies = [...new Set(['USD', 'INR', localCurrency])].filter(Boolean);

  // Default both create + join currency to the user's own currency.
  useEffect(() => {
    setCurrency(localCurrency);
    setJoinCurrency(localCurrency);
  }, [localCurrency]);

  const canCreate = !!(amount && players && (isRate || points)) && isConnected && !busy;
  // switching game type clears the amount / players so an amount from the other game can never be sent
  const pickGameMode = (m) => { setGameMode(m); setAmount(null); setPlayers(null); setError(''); };
  const canJoin = joinCode.trim().length >= 4 && isConnected && !busy;
  const reset = () => { setError(''); setBusy(false); };

  const handleCreate = async () => {
    if (!canCreate) return;
    setBusy(true); setError('');
    try {
      const result = await emitWithPromise('create-custom-room', {
        amount, players, points: isRate ? RATE_MAX_POINTS : points, currency, gameMode
      }, 15000);
      if (result && result.success && result.room && result.room.roomCode) {
        navigate(`/game/${result.room.id}`, {
          state: {
            isCustomRoom: true,
            isCreator: true,
            roomCode: result.room.roomCode,
            gameId: result.room.id,
            amount: result.room.amount,
            currency: result.room.currency,
            maxPlayers: result.room.maxPlayers,
            playerCount: result.room.playerCount,
            maxPoints: result.room.points,
            gameMode: result.room.gameMode
          }
        });
      } else {
        setError(result?.error || 'Failed to create room');
      }
    } catch (e) {
      setError(e.message || 'Failed to create room');
    } finally {
      setBusy(false);
    }
  };

  const handleJoin = async () => {
    if (!canJoin) return;
    setBusy(true); setError('');
    try {
      const code = joinCode.trim().toUpperCase();
      const result = await emitWithPromise('join-custom-room', { code, currency: joinCurrency }, 15000);
      if (result && result.success && result.room && result.room.roomCode) {
        navigate(`/game/${result.room.id}`, {
          state: {
            isCustomRoom: true,
            isCreator: String(result.room.creatorId) === String(currentUser?.id),
            roomCode: result.room.roomCode,
            gameId: result.room.id,
            amount: result.room.amount,
            currency: result.room.currency,
            maxPlayers: result.room.maxPlayers,
            playerCount: result.room.playerCount,
            maxPoints: result.room.points,
            gameMode: result.room.gameMode
          }
        });
      } else {
        setError(result?.error || 'Failed to join room');
      }
    } catch (e) {
      setError(e.message || 'Failed to join room');
    } finally {
      setBusy(false);
    }
  };


  return (
    <div className="custom-room-modal-overlay" onClick={onClose}>
      <div className="custom-room-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🎪 Custom Room</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {!mode ? (
          <div className="cr-mode-select">
            <button className="cr-mode-btn create" onClick={() => setMode('create')}>
              <span className="cr-mode-icon">➕</span>
              <strong>Create Room</strong>
              <small>Set amount, players & points</small>
            </button>
            <button className="cr-mode-btn join" onClick={() => setMode('join')}>
              <span className="cr-mode-icon">🔑</span>
              <strong>Join with Code</strong>
              <small>Enter a code from a friend</small>
            </button>
          </div>
        ) : mode === 'create' ? (
          <div className="cr-form">
            <div className="cr-section">
              <label>🎮 Game</label>
              <div className="cr-options">
                <button className={`cr-opt ${!isRate ? 'active' : ''}`} onClick={() => pickGameMode('points')}>Pool Rummy</button>
                <button className={`cr-opt ${isRate ? 'active' : ''}`} onClick={() => pickGameMode('rate')}>Points Rummy</button>
              </div>
            </div>

            <div className="cr-section">
              <label>{isRate ? '💰 Amount per point' : '💰 Amount (entry fee)'}</label>
              <div className="cr-options">
                {(isRate ? RATE_AMOUNTS : AMOUNTS).map((a) => (
                  <button key={a} className={`cr-opt ${amount === a ? 'active' : ''}`}
                    onClick={() => setAmount(a)}>${isRate ? a.toFixed(2) : a}</button>
                ))}
              </div>
            </div>

            <div className="cr-section">
              <label>👥 Number of Players</label>
              <div className="cr-options">
                {(isRate ? RATE_PLAYER_COUNTS : PLAYER_COUNTS).map((n) => (
                  <button key={n} className={`cr-opt ${players === n ? 'active' : ''}`}
                    onClick={() => setPlayers(n)}>{n}</button>
                ))}
              </div>
            </div>

            {!isRate && (
              <div className="cr-section">
                <label>🎯 Target Points (Pool Rummy)</label>
                <div className="cr-options">
                  {POINTS.map((p) => (
                    <button key={p} className={`cr-opt ${points === p ? 'active' : ''}`}
                      onClick={() => setPoints(p)}>{p} pts</button>
                  ))}
                </div>
              </div>
            )}

            <div className="cr-section">
              <label>💱 Currency</label>
              <div className="cr-options">
                {availableCurrencies.map((c) => (
                  <button key={c} className={`cr-opt ${currency === c ? 'active' : ''}`}
                    onClick={() => setCurrency(c)}>{c}</button>
                ))}
              </div>
            </div>

            <div className="cr-summary">
              <p>{isRate ? 'Per point' : 'Entry'}: <strong>{amount ? `$${isRate ? amount.toFixed(2) : amount}` : '—'}</strong>
                {amount && currency !== 'USD' && (
                  <span className="cr-converted"> ≈ {currencyService.format(currencyService.convertFromUSD(amount, currency), currency)}</span>
                )}
              </p>
              <p>Players: <strong>{players || '—'}</strong></p>
              {isRate ? (
                <>
                  <p>One hand · max <strong>{RATE_MAX_POINTS}</strong> points</p>
                  {amount && (
                    <p>Minimum balance: <strong>${(RATE_MAX_POINTS * amount).toFixed(2)}</strong>
                      {currency !== 'USD' && (
                        <span className="cr-converted"> ≈ {currencyService.format(currencyService.convertFromUSD(RATE_MAX_POINTS * amount, currency), currency)}</span>
                      )}
                    </p>
                  )}
                  <p className="cr-note">Nothing is charged now. After the hand, each player pays points × amount; the winner gets the total minus 10% commission.</p>
                </>
              ) : (
                <>
                  <p>Points: <strong>{points || '—'}</strong></p>
                  <p className="cr-note">10% commission deducted from the prize pool.</p>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="cr-form">
            <div className="cr-section">
              <label>🔑 Enter Room Code</label>
              <input
                className="cr-code-input"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="e.g. ABC123"
                maxLength={6}
              />
            </div>
            <div className="cr-section">
              <label>💱 Pay from Wallet</label>
              <div className="cr-options">
                {availableCurrencies.map((c) => (
                  <button
                    key={c}
                    className={`cr-opt ${joinCurrency === c ? 'active' : ''}`}
                    onClick={() => setJoinCurrency(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {availableCurrencies.map((c) => (
                  <span
                    key={c}
                    style={{
                      fontSize: '0.78rem',
                      color: '#64748b',
                      background: '#f1f5f9',
                      padding: '2px 10px',
                      borderRadius: '10px'
                    }}
                  >
                    {c}: {currencyService.format(walletBalances?.[getBalanceKey(c)] || 0, c)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {error && <div className="cr-error">⚠️ {error}</div>}
        {!isConnected && <div className="cr-error">🔴 Not connected. Please wait…</div>}

        <div className="modal-actions">
          <button type="button" className="cancel-btn" onClick={onClose} disabled={busy}>Cancel</button>
          {mode === 'create' && (
            <button type="button" className="create-btn" onClick={handleCreate} disabled={!canCreate}>
              {busy ? 'Creating…' : '🎪 Create Room'}
            </button>
          )}
          {mode === 'join' && (
            <button type="button" className="create-btn" onClick={handleJoin} disabled={!canJoin}>
              {busy ? 'Joining…' : '🔑 Join Room'}
            </button>
          )}
          {mode && (
            <button type="button" className="cancel-btn"
              onClick={() => { setMode(null); reset(); }} disabled={busy}>← Back</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomRoomModal;
